import React, { useEffect, useState } from 'react';
import CompanyForm from '../components/CompanyForm';
import CompanyKeyProviders from '../components/CompanyKeyProviders';

import SlidingPane from 'react-sliding-pane';

import {
  saveCompanyKeyProviders,
  getCompanyById,
} from '../../../actions/companyActions';
import { getKeyProviders } from '../../../actions/keyProviderActions';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import Loading from '../../common/Loading';
import Delete from './Delete';
import ResponsiveActionBarButtons from '../../common/ResponsiveActionBarButtons';
import DisableKeys from '../../keys/slideout/DisableKeys';
import SlideoutLayout from '../../layout/slideout/SlideoutLayout';

import image_no_content from '../../../content/images/no-content.svg';
import Button from '../../common/Button';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { saveCompany } from '../../../reducers/companyReducer';
import { getAllCompanyTypes } from '../../../reducers/companyTypeReducer';

export interface EditCompanyProps {
  done: (d?: any) => void;
  cancel: () => void;
  id: string | undefined;
}

const EditCompany = ({ done, cancel, id }: EditCompanyProps) => {
  const dispatch = useAppDispatch();
  const keyProviders = useAppSelector((state) => state.keyProviders);
  const companyTypes = useAppSelector((state) => state.companyTypes);

  const { t } = useTranslation();
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    getValues,
    setValue,
  } = useForm();
  const [disableKeysSlideoutOpen, setDisableKeysSlideoutOpen] = useState(false);
  const [deleteSlideoutOpen, setDeleteSlideoutOpen] = useState(false);
  const [file, setFile] = useState<any>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<any>();

  useEffect(() => {
    if (!companyTypes) {
      dispatch(getAllCompanyTypes()).catch(() => {
        toast.error(t('Failed to get company types'));
      });
    }
  }, [companyTypes, dispatch, t]);

  useEffect(() => {
    if (id) {
      getCompanyById(id)
        .then((d) => {
          setCompany(d);
        })
        .catch((e) => {
          console.error(e);
          navigate('/companies');
          toast.error(t('Company not found'));
        });
    }
    if (!keyProviders) {
      dispatch(getKeyProviders());
    }
  }, [id, keyProviders, dispatch, navigate, t]);

  const onSubmit = (data: any) => {
    if (!company) {
      toast.error(t('Failed to save company'));
      return;
    }
    const updatedCompany = { ...company, ...data, file };
    dispatch(saveCompany(updatedCompany))
      .then(() => {
        saveCompanyKeyProviders(company.id, company.companyKeyProviders).then(
          () => {
            setCompany(updatedCompany);
            toast.success(t('Company saved'));
            done(updatedCompany);
          }
        );
      })
      .catch((e) => {
        console.error(e);
        toast.error(t('Failed to save company'));
      });
  };

  const handleKeyProviderChange = (kp: any) => {
    if (kp && company) {
      let companyKeyProviders = [...(company.companyKeyProviders || [])];
      const index = companyKeyProviders.findIndex((x: any) => x.id === kp.id);
      if (index > -1) {
        companyKeyProviders[index].reference = kp.reference;
      } else {
        companyKeyProviders.push({
          id: kp.id,
          name: kp.name,
          reference: kp.reference,
        });
      }
      setCompany({ ...company, companyKeyProviders });
    }
  };

  const setDomains = (domains: string[]) => {
    setCompany({ ...company, domains });
  };

  return company ? (
    <>
      <SlideoutLayout
        title={t('Edit {company}', { company: company.companyName })}
        cancel={cancel}
        bar={
          <ResponsiveActionBarButtons
            buttons={[
              <Button
                key="disableKeys"
                className="btn btn-danger ms-2 d-none d-md-inline-flex"
                onClick={() => setDisableKeysSlideoutOpen(true)}
                text={t('Disable keys')}
              />,
              <Button
                key="delete"
                className="btn btn-danger ms-2 d-none d-md-inline-flex"
                onClick={() => setDeleteSlideoutOpen(true)}
                isDisabled={company.isDeleted}
                title={company.isDeleted ? t('Company is deleted') : t('Delete')}
                text={t('Delete')}
              />,
              <Button
                testId="save-company"
                key="save"
                className="btn btn-primary ms-2"
                onClick={handleSubmit(onSubmit)}
                isDisabled={company.isDeleted}
                title={company.isDeleted ? t('Company is deleted') : t('Save')}
                text={t('Save')}
              />,
            ]}
          />
        }
      >
        {!company.isDeleted ? (
          <div className="row">
            <div className="col-sm-12">
              <CompanyForm
                company={company}
                companyTypes={companyTypes || []}
                register={register}
                errors={errors}
                control={control}
                setValue={setValue}
                getValues={getValues}
                columnCssClass="col-12"
                setFile={setFile}
                setDomains={setDomains}
              ></CompanyForm>
              <CompanyKeyProviders
                keyProviders={keyProviders}
                companyKeyProviders={company?.companyKeyProviders}
                onChange={handleKeyProviderChange}
              />
            </div>
          </div>
        ) : (
          <div
            className="alert alert-info text-center"
            style={{ padding: '30px 15px' }}
          >
            <img
              src={image_no_content}
              alt={t('This company has been deleted')}
              className="space-bottom-small"
            />
            <p>{t('This company has been deleted')}</p>
          </div>
        )}
        <SlidingPane
          isOpen={deleteSlideoutOpen}
          hideHeader={true}
          from="right"
          className="small-side-panel"
          onRequestClose={() => setDeleteSlideoutOpen(false)}
        >
          <Delete
            id={id}
            done={() => {
              navigate('/companies/');
            }}
            cancel={() => setDeleteSlideoutOpen(false)}
          />
        </SlidingPane>

        <SlidingPane
          isOpen={disableKeysSlideoutOpen}
          hideHeader={true}
          from="right"
          className="side-panel"
          onRequestClose={() => setDisableKeysSlideoutOpen(false)}
        >
          <DisableKeys
            title={t('Disable keys for {company}', {
              company: company.companyName,
            })}
            targetCompanyId={id}
            done={() => {
              setDisableKeysSlideoutOpen(false);
              done();
            }}
            cancel={() => setDisableKeysSlideoutOpen(false)}
            defaultReason={t('Disable keys for {company}', {
              company: company.companyName,
            })}
          />
        </SlidingPane>
      </SlideoutLayout>
    </>
  ) : (
    <Loading></Loading>
  );
};

export default EditCompany;
